import React, { createContext, useContext, useEffect, useState } from 'react';
import { useApp } from './AppContext';
import { keyFromLabel } from '../../../../../../../shared/lib/utils';
import { ReservableAddon, ReservablePackage } from '../../../../../../../shared/types/reservable';
import { FullScreenLoader } from '../components/FullScreenLoader';
import {
  CartSessionCustomer,
  ReservationDraftTicketSelection,
  WorkingReservationDraft,
  CartSessionResponse,
  TicketSelection,
} from '../../../../../../../shared/types/cartSession';
import { readQs } from '../../../../../../../shared/lib/querystring';
import qs from 'query-string';
import { useDeleteReservation } from '../../../hooks/useDeleteReservation';
import { useCancelCartSession } from '../../../hooks/useCancelReservation';
import { useCartSession } from '../../../hooks/useCartSession';
import { useInitiateCheckout } from '../../../hooks/useInitiateCheckout';
import { useStartReservation } from '../../../hooks/useStartReservation';
import { useUpdateReservation } from '../../../hooks/useUpdateReservation';
import { ReserveCacheSlot } from '../../../../../../../shared/types/reserveCache';
import Alert from '../components/Alert';

interface CartCacheContextProps {
  cart: CartSessionResponse | null;
  loading: boolean;
  saveCart: (params?: NewCartParams) => Promise<void>;
  saveCustomer: () => Promise<void>;
  deleteCart: (softDelete?: boolean) => void;
  payCart: () => Promise<string | null>;
  activeDraft: WorkingReservationDraft | null;
  swapDrafts: (reservation: WorkingReservationDraft) => void;
  deleteDraft: (reservationDraftId: string) => void;
  passiveUpdateSlot: (slot: ReserveCacheSlot) => void;
  passiveUpdateTickets: (tickets: ReservationDraftTicketSelection[]) => void;
  passiveUpdateTicketField: (
    ticketIdx: number,
    valueIdx: number,
    label: string,
    value: string
  ) => void;
  passiveUpdateReservationField: (label: string, value: string) => void;
  passiveUpdatePackage: (pkg: ReservablePackage, quantity: number) => void;
  passiveUpdateAddon: (addon: ReservableAddon, quantity: number) => void;
  passiveUpdateCustomer: (key: keyof CartSessionCustomer, value: string) => void;
}

interface NewCartParams {
  eventTemplateId: string;
  slotKey: string;
  tickets: TicketSelection[];
}

const LOCAL_STORAGE_KEY = '@GO_EMBED_CART_SESSION_V1';

const CartCacheContext = createContext<CartCacheContextProps | undefined>(undefined);

function cartIsExpired(nextCart: CartSessionResponse) {
  const { status, expiresAt } = nextCart;

  // Treat non-active sessions as not reusable
  if (status !== 'active') return true;

  // expiresAt is typed as Date, but it may arrive as a string depending on serialization
  return Date.now() > new Date(expiresAt).getTime();
}

function getActiveDraft(cart: CartSessionResponse | null): WorkingReservationDraft | null {
  if (!cart) return null;

  return (
    cart.workingDrafts.find(rd => rd.reservationDraft._id === cart.lastReservationDraftUpdated) ||
    cart.workingDrafts[0] ||
    null
  );
}

interface CartBundle {
  _cartId: string | null;
  cart: CartSessionResponse | null;
  activeDraft: WorkingReservationDraft | null;
}
interface WithCartCachePropsBase {}

type WithCartCacheProps = React.PropsWithChildren<WithCartCachePropsBase>;

function extractErrorMessage(err: any): string {
  return (err?.response?.data?.message || err?.message || 'Something went wrong. Please try again.')
    .split('In response to:')[0]
    .trim();
}

export function WithCartCache(props: WithCartCacheProps) {
  const { config } = useApp();
  const { propertyId } = config;

  const [error, setError] = useState<string | null>(null);

  const [cartBundle, _setCartBundle] = useState<CartBundle>({
    _cartId: localStorage.getItem(LOCAL_STORAGE_KEY),
    cart: null,
    activeDraft: null,
  });

  const isConfirmationView = !!readQs().go_success;
  const cartHook = useCartSession(propertyId, cartBundle._cartId || '', {
    enabled: !!cartBundle._cartId && !isConfirmationView,
  });

  const startCartHook = useStartReservation();
  const updateCartHook = useUpdateReservation();
  const deleteCartHook = useCancelCartSession();
  const deleteDraftHook = useDeleteReservation();
  const paymentHook = useInitiateCheckout();

  function setCartBundle(newCartBundle: CartBundle) {
    _setCartBundle(newCartBundle);

    if (newCartBundle._cartId) {
      localStorage.setItem(LOCAL_STORAGE_KEY, newCartBundle._cartId);
    } else {
      localStorage.removeItem(LOCAL_STORAGE_KEY);
    }
  }

  function clearCart() {
    setCartBundle({ _cartId: null, cart: null, activeDraft: null });
  }

  // Update local event state when cartHook data changes
  useEffect(() => {
    if (!cartHook.data) return;
    if (cartIsExpired(cartHook.data)) {
      clearCart();
      return;
    }

    setCartBundle({
      _cartId: cartHook.data._id,
      cart: cartHook.data,
      activeDraft: getActiveDraft(cartHook.data),
    });
  }, [cartHook.data]);

  useEffect(() => {
    if (!cartHook.error) return;

    clearCart();
  }, [cartHook.error]);

  // Periodically check if the cart has expired and clear it if so.
  useEffect(() => {
    if (!cartBundle.cart) return;

    const checkExpiration = () => {
      if (!cartBundle.cart) return;
      if (!cartIsExpired(cartBundle.cart)) return;

      clearCart();
    };

    checkExpiration();

    const intervalId = window.setInterval(checkExpiration, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [cartBundle]);

  async function saveCart(newReservation?: NewCartParams) {
    setError(null);
    try {
      if (newReservation) {
        const result = await startCartHook.mutateAsync({
          property: propertyId,
          cartSessionId: cartBundle._cartId || undefined,
          eventTemplateId: newReservation.eventTemplateId,
          slotKey: newReservation.slotKey,
          tickets: newReservation.tickets,
          acceptedTermsAndConditions: true,
        });

        setCartBundle({
          _cartId: result._id,
          cart: result,
          activeDraft: getActiveDraft(result),
        });
      } else if (cartBundle.activeDraft && cartBundle.cart) {
        const result = await updateCartHook.mutateAsync({
          propertyId,
          cartSessionId: cartBundle.cart._id,
          reservationDraftId: cartBundle.activeDraft.reservationDraft._id,
          customer: cartBundle.cart.customer,
          draft: {
            values: cartBundle.activeDraft.reservationDraft.values ?? {},
            tickets:
              cartBundle.activeDraft.reservationDraft.tickets?.map(t => ({
                ticket: t.ticket,
                quantity: t.quantity,
                values: t.values,
              })) ?? [],
            packages:
              cartBundle.activeDraft.reservationDraft.packages?.map(p => ({
                package: p.package,
                quantity: p.quantity,
              })) ?? [],
            addons:
              cartBundle.activeDraft.reservationDraft.addons?.map(a => ({
                addon: a.addon,
                quantity: a.quantity,
              })) ?? [],
          },
        });

        setCartBundle({
          _cartId: result._id,
          cart: result,
          activeDraft: getActiveDraft(result),
        });
      }
    } catch (err: any) {
      setError(extractErrorMessage(err));
      throw err;
    }
  }

  async function saveCustomer() {
    if (!cartBundle.activeDraft || !cartBundle.cart) return;
    setError(null);
    try {
      const result = await updateCartHook.mutateAsync({
        propertyId,
        cartSessionId: cartBundle.cart._id,
        reservationDraftId: cartBundle.activeDraft.reservationDraft._id,
        customer: cartBundle.cart.customer,
      });
      setCartBundle({
        _cartId: result._id,
        cart: result,
        activeDraft: getActiveDraft(result),
      });
    } catch (err: any) {
      setError(extractErrorMessage(err));
      throw err;
    }
  }

  async function payCart() {
    setError(null);

    if (!cartBundle.cart) return null;

    try {
      const queries = readQs();
      const successQs = qs.stringify({ ...queries, go_success: cartBundle.cart._id });
      const cancelQs = qs.stringify({ ...queries, go_cancel: cartBundle.cart._id });

      const result = await paymentHook.mutateAsync({
        cartSessionId: cartBundle.cart._id,
        notes: '',
        successUrl: `${config.checkoutRedirectUrl.replace(':property', propertyId)}?${successQs}`,
        cancelUrl: `${config.checkoutRedirectUrl.replace(':property', propertyId)}?${cancelQs}`,
      });

      const newCart = { ...cartBundle.cart, ...result.cartSession };

      setCartBundle({
        _cartId: newCart._id,
        cart: newCart,
        activeDraft: getActiveDraft(newCart),
      });

      return newCart.checkoutUrl ?? null;
    } catch (err: any) {
      setError(extractErrorMessage(err));
      throw err;
    }
  }

  function deleteCart(softDelete?: boolean) {
    if (cartBundle.cart && !softDelete) {
      deleteCartHook.mutateAsync({
        cartSessionId: cartBundle.cart._id,
      });
    }
    clearCart();
  }

  async function deleteDraft(reservationDraftId: string) {
    if (!cartBundle.cart) return;
    if (cartBundle.cart.workingDrafts.length < 2) return;

    await deleteDraftHook.mutateAsync({
      cartSessionId: cartBundle.cart._id,
      reservationDraftId,
      propertyId,
    });

    await cartHook.refetch();
  }

  function swapDrafts(reservation: WorkingReservationDraft) {
    setCartBundle({ ...cartBundle, activeDraft: reservation });
  }

  function passiveUpdateSlot(slot: ReserveCacheSlot) {
    if (!cartBundle.activeDraft) return;

    const { reservationDraft } = cartBundle.activeDraft;

    const newActiveReservation: WorkingReservationDraft = {
      ...cartBundle.activeDraft,
      reservationDraft: {
        ...reservationDraft,
        date: slot.date,
        time: slot.time,
        duration: slot.duration,
        host: slot?.host,
        rental: slot?.rental,
      },
    };

    setCartBundle({ ...cartBundle, activeDraft: newActiveReservation });
  }

  function passiveUpdateTickets(tickets: ReservationDraftTicketSelection[]) {
    if (!cartBundle.activeDraft) return;

    const { reservationDraft } = cartBundle.activeDraft;

    const newActiveReservation: WorkingReservationDraft = {
      ...cartBundle.activeDraft,
      reservationDraft: {
        ...reservationDraft,
        tickets,
      },
    };

    setCartBundle({ ...cartBundle, activeDraft: newActiveReservation });
  }

  function passiveUpdateTicketField(
    ticketIdx: number,
    valueIdx: number,
    label: string,
    value: string
  ) {
    if (!cartBundle.activeDraft) return;

    const { reservationDraft } = cartBundle.activeDraft;

    const existingTickets = reservationDraft.tickets ?? [];
    const ticket = existingTickets[ticketIdx];

    if (!ticket) return;

    // Ensure values array matches quantity length
    const valuesArray = ticket.values ?? [];
    const paddedValues = Array.from({ length: ticket.quantity }, (_, i) => valuesArray[i] || {});

    // Update the specific ticket's value at the given index
    const updatedValues = paddedValues.map((val, i) =>
      i === valueIdx ? { ...val, [keyFromLabel(label)]: value } : val
    );

    // Update the ticket with new values
    const updatedTickets = existingTickets.map((t, i) =>
      i === ticketIdx ? { ...t, values: updatedValues } : t
    );

    const newActiveReservation: WorkingReservationDraft = {
      ...cartBundle.activeDraft,
      reservationDraft: {
        ...reservationDraft,
        tickets: updatedTickets,
      },
    };

    setCartBundle({ ...cartBundle, activeDraft: newActiveReservation });
  }

  function passiveUpdateReservationField(label: string, value: string) {
    if (!cartBundle.activeDraft) return;

    const { reservationDraft } = cartBundle.activeDraft;

    const newActiveReservation: WorkingReservationDraft = {
      ...cartBundle.activeDraft,
      reservationDraft: {
        ...reservationDraft,
        values: {
          ...(reservationDraft.values ?? {}),
          [keyFromLabel(label)]: value,
        },
      },
    };

    setCartBundle({ ...cartBundle, activeDraft: newActiveReservation });
  }

  function passiveUpdatePackage(pkg: ReservablePackage, quantity: number) {
    if (!cartBundle.activeDraft) return;

    const { reservationDraft } = cartBundle.activeDraft;

    const existingPackages = reservationDraft.packages ?? [];
    const existingPackageIndex = existingPackages.findIndex(p => p.package._id === pkg._id);

    let updatedPackages;

    if (quantity === 0) {
      // remove
      updatedPackages = existingPackages.filter(p => p.package._id !== pkg._id);
    } else if (existingPackageIndex >= 0) {
      // update
      updatedPackages = existingPackages.map(p =>
        p.package._id === pkg._id ? { ...p, quantity } : p
      );
    } else {
      // create
      updatedPackages = [...existingPackages, { package: pkg, quantity }];
    }

    const newActiveReservation: WorkingReservationDraft = {
      ...cartBundle.activeDraft,
      reservationDraft: {
        ...reservationDraft,
        packages: updatedPackages,
      },
    };

    setCartBundle({ ...cartBundle, activeDraft: newActiveReservation });
  }

  function passiveUpdateAddon(addon: ReservableAddon, quantity: number) {
    if (!cartBundle.activeDraft) return;

    const { reservationDraft } = cartBundle.activeDraft;

    const existingAddons = reservationDraft.addons ?? [];
    const existingAddonIndex = existingAddons.findIndex(a => a.addon._id === addon._id);

    let updatedAddons;

    if (quantity === 0) {
      // remove
      updatedAddons = existingAddons.filter(a => a.addon._id !== addon._id);
    } else if (existingAddonIndex >= 0) {
      // update
      updatedAddons = existingAddons.map(a => (a.addon._id === addon._id ? { ...a, quantity } : a));
    } else {
      // create
      updatedAddons = [...existingAddons, { addon: addon, quantity }];
    }

    const newActiveReservation: WorkingReservationDraft = {
      ...cartBundle.activeDraft,
      reservationDraft: {
        ...reservationDraft,
        addons: updatedAddons,
      },
    };

    setCartBundle({ ...cartBundle, activeDraft: newActiveReservation });
  }

  function passiveUpdateCustomer(key: keyof CartSessionCustomer, value: string) {
    if (!cartBundle.cart) return;

    const currentCustomer = {
      ...(cartBundle.cart.customer || {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
      }),
    };

    currentCustomer[key] = value;

    setCartBundle({
      ...cartBundle,
      cart: {
        ...cartBundle.cart,
        customer: currentCustomer,
      },
    });
  }

  return (
    <CartCacheContext.Provider
      value={{
        cart: cartBundle.cart,
        loading: cartHook.isLoading,
        saveCart,
        saveCustomer,
        deleteCart,
        payCart,
        activeDraft: cartBundle.activeDraft,
        swapDrafts,
        deleteDraft,
        passiveUpdateSlot,
        passiveUpdateTickets,
        passiveUpdateTicketField,
        passiveUpdateReservationField,
        passiveUpdatePackage,
        passiveUpdateAddon,
        passiveUpdateCustomer,
      }}
    >
      <div className="relative">
        {error && (
          <div className="px-4 pb-4">
            <Alert onDismiss={() => setError(null)}>{error}</Alert>
          </div>
        )}
        {props.children}
      </div>
    </CartCacheContext.Provider>
  );
}

export function useCartCache() {
  const context = useContext(CartCacheContext);
  if (context === undefined) {
    throw new Error('useCartCache must be used within a CartCacheProvider');
  }
  return context;
}

interface ForceCartOrFallbackErrorProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export function ForceCartOrFallbackError({ children, fallback }: ForceCartOrFallbackErrorProps) {
  const { cart, activeDraft, loading } = useCartCache();

  if (loading) {
    return <FullScreenLoader className="border-t border-border" />;
  }

  if (!cart || !activeDraft) {
    return (
      <div className="p-6 bg-white">
        {fallback || (
          <Alert>
            Unable to load your cart. Please refresh the page and try again. Some items may no
            longer be available.
          </Alert>
        )}
      </div>
    );
  }

  return <>{children}</>;
}

type ForcedCartCacheContextProps = CartCacheContextProps & {
  cart: CartSessionResponse;
  activeDraft: WorkingReservationDraft;
};

export function useForcedCartCache(): ForcedCartCacheContextProps {
  const context = useContext(CartCacheContext);

  if (context === undefined) {
    throw new Error('useCartCache must be used within a CartCacheProvider');
  }

  if (context.cart == null) {
    throw new Error('useForcedCartCache must be used when a cart is present');
  }

  if (context.activeDraft == null) {
    throw new Error('useForcedCartCache must be used when an active reservation is present');
  }

  return context as ForcedCartCacheContextProps;
}
